'use client';

import { faLocationDot, faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons';

import { InitialLocationSuggestions } from './initial-location-suggestions.component';
import { LocationSearchResults } from './location-search-results.component';
import { RecentSearchedLocations } from './recent-searched-locations.component';
import { RegisterAddress } from './register-address.component';

import { Button } from '@/components/ui/button/button.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Input } from '@/components/ui/input/input.component';
import Banner from '@/components/ui/banner.component';
import { AutocompletePrediction, Place } from '@/types/google-maps.types';
import { CustomerAddress } from '@/types/auth-base.types';
import { Typography } from '@/components/ui/typography/typography.component';
import { SpinnerColor } from '@/components/ui/spinner.component';
import { cn } from '@/utils/cn-tw-merge';

const MIN_SEARCH_LENGTH = 3;

interface LocationModalContentProps {
  searchDisplayValue: string;
  searchResults: AutocompletePrediction[];
  isLoading: boolean;
  shouldShowEmptyResultsMessage: boolean;
  hasLocationError: boolean;
  hasGeolocationError: boolean;
  isGeolocating: boolean;
  deniedGeolocation: boolean;
  isLoggedIn: boolean;
  recentSearchesCount: number;
  currentLocationLabel: string;
  onCurrentLocationClick: () => void;
  onSearchInput: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onUseMyLocation: () => Promise<void>;
  onLocationClick: (prediction: AutocompletePrediction) => Promise<void>;
  onRegisterAddressClick: (address: CustomerAddress) => Promise<void>;
  onSuggestionClick: (place: Place) => Promise<void>;
  onResetSearchResults: () => void;
}

export const LocationModalContent = ({
  searchDisplayValue,
  searchResults,
  isLoading,
  shouldShowEmptyResultsMessage,
  hasLocationError,
  hasGeolocationError,
  isGeolocating,
  deniedGeolocation,
  isLoggedIn,
  recentSearchesCount,
  currentLocationLabel,
  onCurrentLocationClick,
  onSearchInput,
  onUseMyLocation,
  onLocationClick,
  onRegisterAddressClick,
  onSuggestionClick,
  onResetSearchResults,
}: LocationModalContentProps) => {
  const hasPredictions = searchResults.length > 0;
  const hasRecentSearches = recentSearchesCount > 0;

  const shouldShowRecentSearched = hasRecentSearches && !searchDisplayValue;
  const shouldShowRegisterAddress = !searchDisplayValue && isLoggedIn;
  const shouldShowLocationSearchResults = !shouldShowRecentSearched && hasPredictions;
  const shouldShowInitialSuggestions
    = !shouldShowRecentSearched
      && !searchDisplayValue
      && !hasPredictions
      && !isLoggedIn;
  const shouldShowEmptyResults
    = !hasPredictions
      && searchDisplayValue.length >= MIN_SEARCH_LENGTH
      && shouldShowEmptyResultsMessage;
  const shouldShowGeolocationError = deniedGeolocation || hasGeolocationError;
  const shouldShowCurrentLocation = currentLocationLabel.length > 0;

  const containerClassName = 'flex flex-col gap-4 pt-4 px-5 pb-6';

  return (
    <div
      className={containerClassName}
      data-testid={DataTestIdEnum.LOCATION_SELECTION_MODAL}
    >
      <div className="flex flex-col gap-4">
        {shouldShowGeolocationError && (
          <Banner icon={faCircleXmark} iconSize="lg" variant="danger">
            Não foi possível localizar sua cidade. Use o campo abaixo.
          </Banner>
        )}

        {hasLocationError && (
          <Banner icon={faCircleXmark} iconSize="lg" variant="danger">
            Não foi possível buscar cidades agora. Tente novamente em instantes.
          </Banner>
        )}

        <div className="flex flex-col items-start gap-3">
          <Input
            key="location-search-input"
            data-testid={DataTestIdEnum.HOME_LOCATION_SEARCH_INPUT}
            value={searchDisplayValue}
            startIcon={faMagnifyingGlass}
            placeholder="Busque pela cidade, bairro ou rua"
            disabled={isGeolocating}
            onChange={onSearchInput}
          />
          {searchDisplayValue.length > 0
            && searchDisplayValue.length < MIN_SEARCH_LENGTH && (
            <span className="text-sm text-gray-500">
              Digite pelo menos
              {' '}
              {MIN_SEARCH_LENGTH}
              {' '}
              caracteres para buscar.
            </span>
          )}
        </div>

        {shouldShowCurrentLocation && (
          <Pill
            data-testid={DataTestIdEnum.LOCATION_CURRENT_CITY_PILL}
            variant="success"
            icon={faLocationDot}
            label={currentLocationLabel}
            hasAction
            action={onCurrentLocationClick}
            className="w-full cursor-pointer font-semibold transition-colors hover:border-success-400 hover:bg-success-200 hover:text-success-800 active:bg-success-300 sm:w-fit"
          />
        )}
      </div>

      {isLoading
        ? (
            <div className="loading loading-lg my-4"></div>
          )
        : (
            <div className="flex flex-col gap-5">
              <div
                className={cn('flex flex-col gap-5', isGeolocating && 'opacity-50')}
                inert={isGeolocating}
              >
                {shouldShowRegisterAddress && (
                  <RegisterAddress handleRegisterAddressClick={onRegisterAddressClick} />
                )}

                <div className="flex flex-col items-start justify-center gap-4 border-b border-gray-300 pb-4">
                  {shouldShowRecentSearched && (
                    <RecentSearchedLocations
                      handleLocationClick={onLocationClick}
                      callback={onResetSearchResults}
                    />
                  )}

                  {shouldShowLocationSearchResults && (
                    <LocationSearchResults
                      searchResults={searchResults}
                      handleLocationClick={onLocationClick}
                    />
                  )}

                  {shouldShowInitialSuggestions && (
                    <InitialLocationSuggestions
                      handleLocationClick={onSuggestionClick}
                    />
                  )}

                  {shouldShowEmptyResults && (
                    <Typography>
                      Sem resultados para essa busca.
                    </Typography>
                  )}
                </div>
              </div>

              {!deniedGeolocation && (
                <Button
                  variant="text"
                  startIcon={faLocationDot}
                  className="text-lg leading-6 px-4 py-3"
                  isLoading={isGeolocating}
                  spinnerColor={SpinnerColor.GREEN}
                  onClick={onUseMyLocation}
                >
                  {isGeolocating ? 'Obtendo sua localização...' : 'Usar minha localização'}
                </Button>
              )}
            </div>
          )}
    </div>
  );
};
